import { cn } from "@/lib/cn";
import { useNow } from "@/hooks/useNow";
import { useSiteStore } from "@/store/site";
import { tr } from "@/lib/i18n";

/** Live-cadence default thresholds (~2-min uplink): amber past one missed
 *  batch, red past ~three. Exported so per-value freshness consumers
 *  (RightNowStrip cells, BessTile) judge ages against the same bands. */
export const LIVE_WARN_AFTER_S = 150;
export const LIVE_STALE_AFTER_S = 360;

/**
 * Shared age classifier (DATA-08): every component that surfaces freshness
 * must bucket ages exactly like the pip does, or the page contradicts
 * itself (pip green while a cell claims stale, or vice versa).
 */
export function classifyAge(
  ageMs: number,
  warnAfterS: number,
  staleAfterS: number,
): "fresh" | "warn" | "stale" {
  const ageS = Math.max(0, ageMs / 1000);
  if (ageS < warnAfterS) return "fresh";
  if (ageS < staleAfterS) return "warn";
  return "stale";
}

/**
 * Trust signal: every "live" panel should display when its underlying
 * subscription last delivered.
 *
 * Thresholds are cadence-aware (OVW-04 / DSGN-12). When the caller passes
 * no explicit warnAfterS/staleAfterS they derive from the store's data
 * source: the 1 Hz mock simulator keeps the tight 5 s / 30 s bands, while
 * live sites batch-uplink roughly every 2 minutes — so amber starts only
 * past one missed batch (150 s) and red past ~three (360 s). Without this
 * the pip sat critical-red for ~75% of every healthy live uplink cycle,
 * training operators to ignore the one staleness signal the page has.
 */
export function FreshnessPip({
  ts,
  warnAfterS,
  staleAfterS,
  showAge = true,
  size = "sm",
  className,
}: {
  ts: number;
  /** Age (s) at which the pip turns amber. Default 5 (mock) / 150 (live). */
  warnAfterS?: number;
  /** Age (s) at which the pip turns red. Default 30 (mock) / 360 (live). */
  staleAfterS?: number;
  showAge?: boolean;
  size?: "sm" | "xs";
  className?: string;
}) {
  const now = useNow(1000);
  // Primitive selector — safe under Zustand v5's useSyncExternalStore.
  const mode = useSiteStore((s) => s.mode);
  const warnAfter = warnAfterS ?? (mode === "mock" ? 5 : LIVE_WARN_AFTER_S);
  const staleAfter = staleAfterS ?? (mode === "mock" ? 30 : LIVE_STALE_AFTER_S);
  const age = Math.max(0, (now - ts) / 1000);
  const state = classifyAge(now - ts, warnAfter, staleAfter);
  const tone = state === "fresh" ? "ok" : state === "warn" ? "warn" : "critical";
  const text =
    age < 1 ? "fresh" :
    age < 60 ? `${Math.round(age)} s ago` :
    age < 3600 ? `${Math.round(age / 60)} m ago` :
    `${Math.round(age / 3600)} h ago`;
  // G26: non-colour trust cue. The dot's SHAPE encodes state so colour-blind
  // operators and grayscale prints can still read it (filled circle = fresh,
  // hollow ring = delayed, square = stale), and an sr-only phrase + age makes
  // the same signal available to screen readers (colour alone is invisible to
  // both). tr() (not the hook) is fine — this re-renders every second.
  const phrase =
    state === "fresh" ? tr("freshness.fresh")
    : state === "warn" ? tr("freshness.delayed")
    : tr("freshness.stale");
  return (
    <span
      title={phrase}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-1.5 py-0.5",
        size === "xs" ? "h-5 text-[9px]" : "h-6 text-[10px]",
        tone === "ok" && "border-ok/30 bg-ok/10 text-ok",
        tone === "warn" && "border-warn/30 bg-warn/10 text-warn",
        tone === "critical" && "border-critical/30 bg-critical/10 text-critical",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "h-1.5 w-1.5",
          state === "fresh" && "rounded-full bg-current animate-pulse-pip",
          state === "warn" && "rounded-full border border-current",
          state === "stale" && "rounded-[1px] bg-current",
        )}
      />
      {showAge && <span aria-hidden="true" className="font-medium num">{text}</span>}
      <span className="sr-only">{phrase}{showAge && age >= 1 ? `, ${text}` : ""}</span>
    </span>
  );
}
